import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/NavigationBar';
import NotificationBanner from '../../components/NotificationBanner';
import ContactTable from './components/ContactTable';
import ContactModal from './components/ContactModal';
import SearchBar from './components/SearchBar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { contactsService } from '../../services/crmService';
import { scoreContactById, scoreBatchContacts } from '../../services/leadScoringService';

const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showNotification, setShowNotification] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [scoringInProgress, setScoringInProgress] = useState(new Set());

  const handleRealtimeUpdate = ({ eventType, new: newData, old: oldData }) => {
    if (eventType === 'INSERT') {
      setContacts(prev => [...prev, newData]);
    } else if (eventType === 'UPDATE') {
      setContacts(prev => prev?.map(contact => contact?.id === newData?.id ? newData : contact));
    } else if (eventType === 'DELETE') {
      setContacts(prev => prev?.filter(contact => contact?.id !== oldData?.id));
    }
  };

  useEffect(() => {
    loadContacts();
    const unsubscribe = contactsService?.subscribeToChanges(handleRealtimeUpdate);
    return () => unsubscribe();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const data = await contactsService?.getAll();
      setContacts(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load contacts');
      console.error('Error loading contacts:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts?.filter(contact => {
    if (!searchTerm) return true;
    const searchLower = searchTerm?.toLowerCase();
    return (
      contact?.name?.toLowerCase()?.includes(searchLower) ||
      contact?.email?.toLowerCase()?.includes(searchLower) ||
      contact?.companyName?.toLowerCase()?.includes(searchLower) ||
      contact?.appointmentDate?.includes(searchLower)
    );
  });

  const sortedContacts = [...filteredContacts]?.sort((a, b) => {
    if (!sortConfig?.key) return 0;
    
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    
    if (aValue < bValue) {
      return sortConfig?.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAddContact = () => {
    setSelectedContact(null);
    setIsModalOpen(true);
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this contact?')) {
      try {
        await contactsService?.delete(contactId);
        setSelectedContacts(prev => prev?.filter(id => id !== contactId));
        setError('');
      } catch (err) {
        setError(err?.message || 'Failed to delete contact');
        console.error('Error deleting contact:', err);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts?.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedContacts?.length} contact(s)?`)) {
      try {
        await Promise.all(selectedContacts?.map(id => contactsService?.delete(id)));
        setSelectedContacts([]);
        setError('');
      } catch (err) {
        setError(err?.message || 'Failed to delete contacts');
        console.error('Error deleting contacts:', err);
      }
    }
  };

  const handleScoreContact = async (contactId) => {
    setScoringInProgress(prev => new Set(prev)?.add(contactId));
    try {
      await scoreContactById(contactId);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to score contact');
    } finally {
      setScoringInProgress(prev => {
        const newSet = new Set(prev);
        newSet?.delete(contactId);
        return newSet;
      });
    }
  };

  const handleBulkScore = async () => {
    if (selectedContacts?.length === 0) return;
    
    setScoringInProgress(prev => new Set([...prev, ...selectedContacts]));
    try {
      const results = await scoreBatchContacts(selectedContacts);
      const failedCount = results?.filter(r => !r?.success)?.length;
      
      if (failedCount > 0) {
        setError(`Scored ${results?.length - failedCount} contacts. ${failedCount} failed.`);
      } else {
        setError('');
      }
      
      setSelectedContacts([]);
    } catch (err) {
      setError(err?.message || 'Failed to score contacts');
    } finally {
      setScoringInProgress(new Set());
    }
  };

  const handleSaveContact = async (contactData) => {
    try {
      if (selectedContact) {
        await contactsService?.update(selectedContact?.id, contactData);
      } else {
        await contactsService?.create(contactData);
      }
      setIsModalOpen(false);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to save contact');
      console.error('Error saving contact:', err);
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Name', 'Email', 'Company Name', 'Appointment Date'],
      ...contacts?.map(c => [c?.name, c?.email, c?.companyName, c?.appointmentDate])
    ]?.map(row => row?.join(','))?.join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  const toggleSelectAll = () => {
    if (selectedContacts?.length === sortedContacts?.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(sortedContacts?.map(c => c?.id));
    }
  };

  const toggleSelectContact = (contactId) => {
    setSelectedContacts(prev =>
      prev?.includes(contactId)
        ? prev?.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      <NavigationBar />
      {error && (
        <NotificationBanner
          message={error}
          type="error"
          onDismiss={() => setError('')}
        />
      )}
      {showNotification && !error && (
        <NotificationBanner
          message="Manage your contacts efficiently with search, sort, and bulk operations."
          type="info"
          onDismiss={() => setShowNotification(false)}
        />
      )}
      <div className="container mx-auto px-4 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                Contacts Management
              </h1>
              <p className="text-muted-foreground">
                Store, edit, and manage your contact information
              </p>
            </div>
            <div className="flex gap-2">
              {selectedContacts?.length > 0 && (
                <>
                  <Button
                    variant="default"
                    onClick={handleBulkScore}
                    iconName="Sparkles"
                    iconPosition="left"
                    disabled={scoringInProgress?.size > 0}
                  >
                    Score ({selectedContacts?.length})
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    iconName="Trash2"
                    iconPosition="left"
                  >
                    Delete ({selectedContacts?.length})
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={handleExport}
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
              <Button
                variant="default"
                onClick={handleAddContact}
                iconName="Plus"
                iconPosition="left"
              >
                Add Contact
              </Button>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by name, email, company, or date..."
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading contacts...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-card rounded-lg shadow-sm border border-border">
              <ContactTable
                contacts={sortedContacts}
                onEdit={handleEditContact}
                onDelete={handleDeleteContact}
                onSort={handleSort}
                sortConfig={sortConfig}
                selectedContacts={selectedContacts}
                onToggleSelect={toggleSelectContact}
                onToggleSelectAll={toggleSelectAll}
                onScoreContact={handleScoreContact}
                scoringInProgress={scoringInProgress}
              />
            </div>

            {sortedContacts?.length === 0 && (
              <div className="text-center py-12">
                <Icon name="Users" size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {searchTerm ? 'No contacts found' : 'No contacts yet'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm
                    ? 'Try adjusting your search criteria' : 'Get started by adding your first contact'}
                </p>
                {!searchTerm && (
                  <Button
                    variant="default"
                    onClick={handleAddContact}
                    iconName="Plus"
                    iconPosition="left"
                  >
                    Add Your First Contact
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <ContactModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        contact={selectedContact}
        onSave={handleSaveContact}
      />
    </div>
  );
};

export default ContactsManagement;